import { useMemo, type ReactElement } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import Tooltip from '@mui/material/Tooltip'
import PermissionGate from '../guards/PermissionGate'
import useAuth from '../../hooks/useAuth'
import useGet from '../../hooks/useGet'
import useNotificationSSE from '../../hooks/useNotificationSSE'
import usePost from '../../hooks/usePost'
import usePermissions from '../../hooks/usePermissions'
import { notificationsService } from '../../services/notifications.service'
import type { NotificationsListData, ReadAllNotificationsData } from '../../types/api'
import { NAV_GROUPS } from './sidebar.constants'
import {
  Footer,
  GroupBlock,
  GroupTitle,
  Groups,
  Header,
  ItemCount,
  ItemLabel,
  ItemRow,
  LinkReset,
  LogoTitle,
  RoleBadge,
  Root,
  SidebarDivider,
  UserAvatar,
  UserEmail,
  UserName,
  UserText,
} from './Sidebar.styles'
import Logo from 'assets/png/logo.png'

interface SidebarProps {
  open: boolean
}

const Sidebar = ({ open }: SidebarProps): ReactElement => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { can } = usePermissions()
  const { pathname } = useLocation()
  const isOnNotificationsPage = pathname === '/notifications'

  useNotificationSSE({ isOnNotificationsPage })
  const notificationsEndpoint = useMemo(() => {
    const params = new URLSearchParams({
      page: '1',
      limit: '1',
    })
    return `${notificationsService.endpoints.list}?${params.toString()}`
  }, [])
  const { data: notificationsData } = useGet<NotificationsListData>(
    [...notificationsService.queryKeys.list, 'sidebar', '1', '1'],
    notificationsEndpoint,
    { enabled: true },
  )
  const unreadCount = notificationsData?.unreadCount
  const readAllNotifications = usePost<ReadAllNotificationsData>({
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationsService.queryKeys.list })
    },
  })

  const visibleGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => can(item.permission)),
      })).filter((group) => group.items.length > 0),
    [can],
  )

  return (
    <Root $open={open}>
      <Header>
        <img src={Logo} alt="PlanIT logo" />
        {open && <LogoTitle>PLANIT</LogoTitle>}
        {open && <RoleBadge>Admin Portal</RoleBadge>}
      </Header>

      <SidebarDivider />

      <Groups>
        {visibleGroups.map((group) => (
          <GroupBlock key={group.title}>
            {open && <GroupTitle>{group.title}</GroupTitle>}
            {group.items.map(({ label, path, permission, icon, badge }) => {
              const resolvedBadge = path === '/notifications' ? unreadCount : badge
              const shouldShowBadge =
                path === '/notifications'
                  ? !isOnNotificationsPage && (resolvedBadge ?? 0) > 0
                  : resolvedBadge !== undefined
              const shouldMarkRead = path === '/notifications' && (unreadCount ?? 0) > 0
              const handleNavigationClick = () => {
                if (!shouldMarkRead || readAllNotifications.isPending) return
                readAllNotifications.mutate({ url: notificationsService.endpoints.readAll })
              }

              return (
                <PermissionGate key={path} permission={permission}>
                  <Tooltip title={!open ? label : ''} placement="right">
                    <LinkReset to={path} onClick={handleNavigationClick}>
                      {({ isActive }) => (
                        <ItemRow $active={isActive} $open={open}>
                          {icon}
                          {open && <ItemLabel $active={isActive}>{label}</ItemLabel>}
                          {open && shouldShowBadge && <ItemCount>{resolvedBadge}</ItemCount>}
                        </ItemRow>
                      )}
                    </LinkReset>
                  </Tooltip>
                </PermissionGate>
              )
            })}
          </GroupBlock>
        ))}
      </Groups>

      <Footer>
        <Tooltip title={user?.email ?? ''}>
          <UserAvatar>{user?.name?.slice(0, 2).toUpperCase() ?? 'SA'}</UserAvatar>
        </Tooltip>
        {open && (
          <UserText>
            <UserName noWrap>{user?.name ?? 'Super Admin'}</UserName>
            <UserEmail noWrap>{user?.email ?? 'admin@planit.io'}</UserEmail>
          </UserText>
        )}
      </Footer>
    </Root>
  )
}

export default Sidebar
